import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import EditorPhoto, { useEditorPhoto } from '../EditorPhoto'
import { eventDefaultImagesKey, TENIS10 } from 'pages/EventsManagement/tenis10Event'

function MediaSlot({ entry, register, locked }) {
  const photo = useEditorPhoto(entry)
  useEffect(() => { register(entry.key, photo) }, [entry.key, photo, register])
  return <EditorPhoto photo={photo} locked={locked} label={entry.label} id={'club-media-' + entry.key.replace(/\./g, '-')} />
}
const ClubEditMedia = forwardRef(function ClubEditMedia({ id, profile, categories = [], locked, onState }, ref) {
  const slots = useRef({}), [state, setState] = useState({})
  const register = useCallback((key, photo) => {
    slots.current[key] = photo
    setState(current => current[key]?.dirty === photo.dirty && current[key]?.uploading === photo.uploading ? current
      : { ...current, [key]: { dirty: photo.dirty, uploading: photo.uploading } })
  }, [])
  const dirty = Object.values(state).some(slot => slot.dirty), uploading = Object.values(state).some(slot => slot.uploading)
  useEffect(() => { onState({ dirty, uploading }) }, [dirty, uploading, onState])
  useImperativeHandle(ref, () => ({ retain: () => Object.values(slots.current).forEach(photo => photo.retain()),
    release: () => Object.values(slots.current).forEach(photo => photo.release()), upload: async () => {
    const result = {}
    for (const [key, photo] of Object.entries(slots.current)) {
      if (!photo.dirty) continue
      if (key.includes('.')) {
        const [category, kind] = key.split('.')
        if (!result.defaultEventImages) result.defaultEventImages = {}
        result.defaultEventImages[category] = { ...result.defaultEventImages[category], [kind]: await photo.upload() }
      } else result[key] = await photo.upload()
    }
    return result
  } }), [])
  // The club's logo and cover live in the folder of its id (the club's _id), where SportyaOS, the web and Clubs read and replace them; its
  // event defaults live in the numeric clubId's folder, where Events copies them from.
  const folder = 'clubs/' + id, defaultsFolder = 'clubs/' + profile.clubId
  // Every category has an adult set and a Tenis10 one (`tenis10-{category}`, Marian 2026-10-29): a Tenis10 event takes the latter.
  const tenis10Key = category => eventDefaultImagesKey({ programType: TENIS10, category })
  const keys = [...new Set([...categories.flatMap(category => [category.key, tenis10Key(category.key)]),
    ...Object.keys(profile.defaultEventImages || {})])].filter(key => /^[a-zA-Z0-9_-]{1,100}$/.test(key))
  // A set is named by its category, a Tenis10 one „Tenis10 · {Category}”, never by its stored key (a category the settings no longer list
  // keeps its key as its name).
  const tenis10Prefix = tenis10Key('')
  const nameOf = key => {
    const tenis10 = key.length > tenis10Prefix.length && key.startsWith(tenis10Prefix), categoryKey = tenis10 ? key.slice(tenis10Prefix.length) : key
    return (tenis10 ? 'Tenis10 · ' : '') + (categories.find(item => item.key === categoryKey)?.name || categoryKey)
  }
  return <section className="card"><div className="card-body"><details><summary>Club images & event defaults</summary>
    <p className="text-muted mt-3">Preview images locally. Uploads begin when you save, and the current images remain available until the profile is saved.</p>
    <div className="club-creation-grid">{[['logoImage', 'Club logo'], ['coverImage', 'Club cover']].map(([key, label]) => <MediaSlot key={key}
      entry={{ key, label, current: profile[key], folder }} register={register} locked={locked} />)}</div>
    {keys.map(category => <details key={category} className="mt-3"><summary>{nameOf(category)} event defaults</summary>
      <div className="club-creation-grid mt-3">{['logo', 'featured', 'cover'].map(kind => <MediaSlot key={kind} register={register} locked={locked}
        entry={{ key: category + '.' + kind, label: nameOf(category) + ' event ' + kind,
          current: profile.defaultEventImages?.[category]?.[kind], folder: defaultsFolder + '/' + category }} />)}</div>
    </details>)}
  </details></div></section>
})
export default ClubEditMedia
